import { useEffect } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import type { Query } from '@/api/query'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Google Search Console — the second card in S-D3's Connections panel (S-B22a).
 *
 * **Its own component rather than more of `ConnectionsPanel`**, because the two
 * connections share a shape and nothing else. LinkedIn's grant is an access token
 * with no refresh and a sixty-day life, so that card's whole job is to prompt a
 * reconnect before it dies. Google's is a refresh token that outlives everything,
 * so this card's job is different: say which property each site resolved to, and
 * make the *"we connected and found nothing of yours"* case obvious rather than
 * leaving somebody staring at an empty dashboard wondering why.
 *
 * **No risk acknowledgement here, and that is deliberate.** LinkedIn's checkbox
 * exists because automating a member's account can get it banned — a risk they
 * have to accept in their own name (§8). Reading your own Search Console data
 * carries no such risk, Google's own consent screen is the disclosure, and
 * inventing a second checkbox would make the LinkedIn one read as boilerplate.
 *
 * **The connection is read by the panel and handed down, rather than fetched
 * here.** It was this card's own `useQuery` until S-G13 put a GA4 card in the
 * same panel over the same grant. Two cards each reading
 * `GET /connections/google` is not merely a duplicated request: pressing
 * Disconnect here refetches *this* card's copy and leaves the other one still
 * rendering a grant that no longer exists. One query, one truth, and `refetch`
 * below now updates both.
 *
 * The three states every component ships (CLAUDE.md, Frontend): loading while the
 * connection is read, an error state with a retry, and an empty state written as
 * an invitation — which is what a freshly provisioned tenant actually sees.
 */

/** Mirrors the outcomes `search_console_callback` redirects with. */
const OUTCOMES: Record<string, { tone: 'success' | 'danger'; title: string; body: string }> = {
  connected: {
    tone: 'success',
    title: 'Search Console connected',
    body: 'AugRank can now read how your site performs in Google search.',
  },
  no_access: {
    tone: 'danger',
    title: 'Connected, but no matching property',
    body:
      'That Google account has no Search Console property for your site. Sign in with a ' +
      'different account, or ask whoever manages the site to give you access.',
  },
  failed: {
    tone: 'danger',
    title: 'Search Console did not finish connecting',
    body:
      'Nothing was changed. This usually means the consent screen was cancelled, or it was ' +
      'left open too long. Try again — it takes a few seconds.',
  },
}

type GoogleConnection = Schema<'SearchConsoleConnectionOut'>

export function SearchConsoleCard({
  connection,
}: {
  connection: Query<GoogleConnection>
}): JSX.Element {
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const start = useMutation(() => call(() => api.POST('/api/v1/connections/google/start', {})))
  const disconnect = useMutation(() => call(() => api.DELETE('/api/v1/connections/google', {})))

  // The callback redirects back here with `?search_console=…`, because a redirect
  // from Google carries no body of ours. Read once, shown as a toast, then
  // stripped from the URL — a reload should not re-announce a connection that
  // happened ten minutes ago.
  const outcome = params.get('search_console')
  useEffect(() => {
    if (outcome === null) return
    const message = OUTCOMES[outcome]
    if (message !== undefined) {
      toast.show({ kind: message.tone, title: message.title, body: message.body })
    }
    params.delete('search_console')
    setParams(params, { replace: true })
    connection.refetch()
    // `outcome` is the only input that should re-run this; re-running it on a
    // refetched connection would show the toast again on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  const data = connection.state.data
  const sites = data?.sites ?? []
  const matched = sites.filter((site) => site.gsc_property !== null && site.gsc_property !== '')

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>Google Search Console</h3>
        <span className="ar-card__spacer" />
        {data?.connected === true && matched.length > 0 ? (
          <span className="ar-badge ar-badge--success">✓ Connected</span>
        ) : data?.connected === true ? (
          <span className="ar-badge ar-badge--warning">⚠ No matching property</span>
        ) : data?.status === 'no_access' ? (
          <span className="ar-badge ar-badge--danger">Access withdrawn</span>
        ) : null}
      </div>

      {/* The inset, same as the two cards above it — see `ConnectionsPanel`. */}
      <div className="ar-card__body">
        <p className="ar-text-body-sm">
          Where your real ranking, click and traffic data comes from. <strong>Read-only</strong> —
          AugRank never submits a sitemap, never changes a property, never writes to Analytics, and
          never touches Ads, Gmail or Drive.
        </p>

        {connection.state.status === 'loading' ? (
          <Loading label="Loading your Search Console connection" />
        ) : connection.state.status === 'error' ? (
          <ErrorState failure={connection.state.failure} onRetry={connection.refetch} />
        ) : data === null ? (
          <p className="ar-empty ar-empty--inline">
            We could not read your Search Console connection just now. Reload the page to try again.
          </p>
        ) : (
          <>
            <InlineError failure={start.state.failure ?? disconnect.state.failure} />

            {data.connected ? (
              <>
                <p className="ar-text-body-sm">
                  Reading as{' '}
                  <strong>{data.account_label ?? 'the Google account you authorised'}</strong>.
                </p>
                {sites.length > 0 ? (
                  <ul className="ar-text-body-sm" style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    {sites.map((site) => (
                      <li key={site.id}>
                        <strong>{site.domain}</strong>{' '}
                        {site.gsc_property !== null && site.gsc_property !== '' ? (
                          <span className="ar-mono">{site.gsc_property}</span>
                        ) : (
                          <span className="ar-muted">
                            — no property yet. <Link to="/seo/search-performance">Choose one</Link>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!data.can_verify_sites ? (
                  <p className="ar-callout ar-text-body-sm" data-tone="warning">
                    You did not grant the site-ownership permission, so AugRank cannot show you a
                    DNS record to prove you own a site Google does not know about yet. Reconnect and
                    accept every permission Google offers if you need it.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="ar-empty ar-empty--inline">
                {data.status === null
                  ? 'Search Console is not connected, so your SEO dashboard has no ranking or click data in it yet.'
                  : data.status === 'pending'
                    ? 'A connection was started and never finished. Connect again — it takes a few seconds.'
                    : 'Google no longer accepts this connection. Connect again to keep reading your search data.'}
              </p>
            )}
            {data.detail !== null ? (
              <p className="ar-text-body-sm ar-muted">Google said: {data.detail}</p>
            ) : null}

            <div className="ar-row" style={{ gap: 'var(--ar-space-3)' }}>
              <Button
                tone={data.connected ? 'ghost' : 'primary'}
                busy={start.pending}
                busyLabel="Opening Google"
                onClick={() => {
                  void (async () => {
                    const result = await start.run(undefined)
                    if (!result.ok) return
                    // A full navigation, not a fetch: the consent screen is
                    // Google's page and has to be rendered by the browser. The
                    // `state` cookie the server just set travels with it.
                    window.location.assign(result.data.url)
                  })()
                }}
              >
                {data.connected ? 'Reconnect' : 'Connect Search Console'}
              </Button>
              {data.connected ? (
                <Button
                  tone="ghost"
                  size="sm"
                  busy={disconnect.pending}
                  busyLabel="Disconnecting"
                  onClick={() => {
                    void (async () => {
                      const result = await disconnect.run(undefined)
                      if (!result.ok) return
                      connection.refetch()
                      toast.show({
                        kind: 'success',
                        title: 'Search Console disconnected',
                        body: 'The stored Google credential is deleted, and so is the link between each of your sites and its property.',
                      })
                    })()
                  }}
                >
                  Disconnect
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </section>
  )
}
